/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import { useLabels } from '@/hooks/useLabels';
import azCommonStyles from '../../../theme/globals.module.scss';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import styles from './styles.module.scss';
import { Button } from '@/components/Button';
import Image from '@/components/NextImage';
import { type VehicleFit } from '@/api/types/browse-search-types';
import { useCallback } from 'react';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { usePageType } from '@/features/category/api/getPageType';
import { type CommerceListItem } from '@/components/Cart/types';
import { FITMENT_ICONS, CAR_FITMENT_STATUS_ICONS } from '@/constants/images/fitmentIcons';
import { getVehicleTypeCategory } from '@/utils/getVehicleTypeCategory';
import { Icon, Text } from '@az/starc-ui';
import { Warning } from '@az/starc-ui-icons';

export type CustomClasses = {
  container?: string;
  button?: string;
  imageContainer?: string;
};

type Props = {
  vehicleFitment: VehicleFit | undefined;
  vehicleDisplayName?: string;
  toggleShowYmme: ((fitment?: VehicleFit) => void) | null;
  isList?: boolean;
  customClass?: CustomClasses;
  productName?: string;
  onClickSeeFittingParts?: () => void;
  displayVehicleInformation?: boolean;
  displayGenericFitmentMessage?: boolean;
  customizedVehicleName?: string | undefined;
  isConfigurableSku?: boolean;
  makeModelYearPageType?: boolean;
  makeOrModelName?: string | null | undefined;
  updateFitment?: (data: CommerceListItem) => void;
  cartProductData?: CommerceListItem;
  fitmentGrammarType?: 'fitsA' | 'fitsYour';
  underlineVehicle?: boolean;
  vehicleTypeName?: string;
  disableVehicleTypeFallback?: boolean;
};

const labelMap = {
  label_change_vehicle: 'hyperlink_YMME_Body_ChangeVehicle',
  label_CategoryPage_body_Check_VechicleSpecific: 'label_CategoryPage_body_Check_VechicleSpecific',
  label_fits_your_vehicle: 'label_fits_your_vehicle',
  label_doesnt_fit_vehicle: 'label_doesnt_fit_vehicle',
  label_fits_a: 'label_ymm_fitsYour_prefix',
  label_fits_your: 'label_fits_your',
  label_see_parts: 'label_fitment_button_see_parts',
  label_fitment_button_if_this_fits: 'label_fitment_button_if_this_fits',
  label_fitment_button_check: 'label_fitment_button_check',
  label_ymm_fitment_prefix: 'label_ymm_fitment_prefix',
  label_doesnt_fit_your: 'label_doesnt_fit_your',
  label_customized_for_your: 'label_customized_for_your',
  label_more_info_needed: 'label_more_info_needed_for_vehicle',
  label_fitment_button_if_this_fits_aces: 'label_fitment_button_if_this_fits_aces',
  mayNotFit: 'label_fitment_may_not_fit',
};

const ACESFitmentButton = ({
  vehicleFitment,
  vehicleDisplayName,
  toggleShowYmme,
  isList = true,
  customClass,
  productName,
  onClickSeeFittingParts,
  displayGenericFitmentMessage = false,
  customizedVehicleName,
  isConfigurableSku,
  makeOrModelName,
  updateFitment,
  cartProductData,
  fitmentGrammarType = 'fitsYour',
  underlineVehicle = true,
  vehicleTypeName,
  disableVehicleTypeFallback,
}: Props) => {
  const { data: pageTypeData } = usePageType();
  const preferredVehicle = usePreferredVehicle();
  // Use provided vehicleTypeName if available, otherwise fall back to preferred vehicle (unless disabled for cart)
  const effectiveVehicleTypeName =
    vehicleTypeName ??
    (disableVehicleTypeFallback
      ? undefined
      : (preferredVehicle?.vehicleTypeName ??
        preferredVehicle?.acesVehicleAttributes?.vehicleQuestionsNames?.vehicleTypeName));
  const vehicleTypeCategory = getVehicleTypeCategory(effectiveVehicleTypeName);
  const nonCarTypeIcon = vehicleTypeCategory !== 'car';
  const labels = useLabels(labelMap);
  const selectedVehicle = vehicleDisplayName
    ? vehicleDisplayName
    : preferredVehicle
      ? `${preferredVehicle?.year} ${preferredVehicle?.make} ${preferredVehicle?.model}`
      : `${pageTypeData?.year} ${pageTypeData?.make} ${pageTypeData?.model}`;
  const [checkWordValue, ifWordValue] =
    labels?.label_CategoryPage_body_Check_VechicleSpecific?.split(' ') ?? [];

  const locale = useLocale();
  const isUsOrMxSite = locale === countryCodes.us || locale === countryCodes.mx;

  const fitmentBadgeClickCallback = useCallback(() => {
    toggleShowYmme?.(vehicleFitment);
    if (cartProductData) {
      updateFitment?.(cartProductData);
    }
  }, [cartProductData, toggleShowYmme, updateFitment, vehicleFitment]);

  let buttonImageSource = null;
  let buttonText = null;
  let testId = null;
  let ariaLabel = '';
  let buttonClassName = null;

  switch (vehicleFitment) {
    case 'FITS':
      if (nonCarTypeIcon) {
        buttonImageSource = FITMENT_ICONS[vehicleTypeCategory].FITS;
      } else {
        buttonImageSource = CAR_FITMENT_STATUS_ICONS.FITS;
      }
      buttonClassName = styles.fitsVehicle;
      testId = !isConfigurableSku
        ? 'fitment-button-fits-vehicle'
        : 'fitment-button-custom-sku-vehicle';

      if (displayGenericFitmentMessage) {
        buttonText = <span className={styles.successText}>{labels.label_fits_your_vehicle}</span>;
      } else if (isConfigurableSku) {
        buttonText = (
          <>
            <span className={styles.successText}>{labels.label_customized_for_your}</span>{' '}
            <span className={underlineVehicle ? styles.underlined : undefined}>
              {customizedVehicleName}
            </span>
          </>
        );
      } else {
        const fitmentLabel =
          fitmentGrammarType === 'fitsYour' ? labels.label_fits_your : labels.label_fits_a;
        buttonText = (
          <>
            <span className={styles.successText}>{fitmentLabel}</span>{' '}
            <span className={underlineVehicle ? styles.underlined : undefined}>
              {selectedVehicle}
            </span>
          </>
        );
      }
      break;
    case 'NEEDS_BASE_VEHICLE':
      buttonImageSource = nonCarTypeIcon
        ? FITMENT_ICONS[vehicleTypeCategory].NEEDS_MORE_INFO
        : CAR_FITMENT_STATUS_ICONS.NEEDS_BASE_VEHICLE;
      testId = 'fitment-button-no-vehicle';
      buttonText = (
        <>
          {isUsOrMxSite && makeOrModelName ? (
            <>
              <span className={underlineVehicle ? styles.underlined : undefined}>
                {labels.label_fitment_button_check}
              </span>{' '}
              {labels.label_ymm_fitment_prefix}
              <strong>{` ${makeOrModelName}`}</strong>
            </>
          ) : (
            <>
              <span className={underlineVehicle ? styles.underlined : undefined}>
                {labels.label_fitment_button_check}
              </span>{' '}
              {labels.label_fitment_button_if_this_fits}
            </>
          )}
        </>
      );
      ariaLabel = `${checkWordValue} ${ifWordValue} ${productName} ${labels.label_fits_your_vehicle}`;
      break;
    case 'NEEDS_MORE_INFO':
      if (nonCarTypeIcon) {
        buttonImageSource = FITMENT_ICONS[vehicleTypeCategory].NEEDS_MORE_INFO;
      } else {
        buttonImageSource = CAR_FITMENT_STATUS_ICONS.NEEDS_MORE_INFO;
      }
      buttonClassName = styles.moreInfoNeeded;
      testId = 'fitment-button-more-info-needed';
      buttonText = (
        <>
          <span className={styles.moreInfoText}>{labels.label_more_info_needed}</span>{' '}
          <span className={underlineVehicle ? styles.underlined : undefined}>
            {selectedVehicle}
          </span>
        </>
      );
      break;
    case 'DOES_NOT_FIT':
    default:
      if (nonCarTypeIcon) {
        buttonImageSource = FITMENT_ICONS[vehicleTypeCategory].DOES_NOT_FIT;
      } else {
        buttonImageSource = CAR_FITMENT_STATUS_ICONS.DOES_NOT_FIT;
      }
      buttonClassName = styles.fitsVehicleError;
      testId = 'fitment-button-doesnt-fit-vehicle';
      buttonText = (
        <div className={styles.errorBtn}>
          {!vehicleDisplayName && (
            <div className={styles.errorText}>{labels.label_doesnt_fit_vehicle}</div>
          )}
          <div>
            {vehicleDisplayName ? (
              <>
                <button
                  type="button"
                  className={cx(
                    styles.inlineButton,
                    styles.changeVehicle,
                    underlineVehicle && styles.underlined,
                  )}
                  onClick={fitmentBadgeClickCallback}
                >
                  <span className={styles.errorText}>{labels.label_doesnt_fit_your}</span>{' '}
                  {vehicleDisplayName}
                </button>
              </>
            ) : null}
            {!vehicleDisplayName && !displayGenericFitmentMessage ? (
              <button
                type="button"
                className={cx(
                  styles.inlineButton,
                  styles.changeVehicle,
                  underlineVehicle && styles.underlined,
                )}
                onClick={fitmentBadgeClickCallback}
              >
                {labels.label_change_vehicle}
              </button>
            ) : null}
            {onClickSeeFittingParts && (
              <>
                <span className={styles.errorPipe}>|</span>
                <button
                  type="button"
                  className={cx(
                    styles.inlineButton,
                    styles.seeFittingPartsBtn,
                    underlineVehicle && styles.underlined,
                  )}
                  onClick={onClickSeeFittingParts}
                >
                  {labels.label_see_parts}
                </button>
              </>
            )}
          </div>
        </div>
      );
      break;
  }

  const isDoesNotFit = vehicleFitment === 'DOES_NOT_FIT' || vehicleFitment === undefined;

  const descChildren = (
    <>
      <div
        className={cx(
          styles.imgContainer,
          nonCarTypeIcon && styles.imgContainerNonCar,
          customClass?.imageContainer,
        )}
      >
        {nonCarTypeIcon ? (
          <Image src={buttonImageSource} alt="" width={32} height={23} />
        ) : (
          <>
            <Image src={CAR_FITMENT_STATUS_ICONS.vehicle} alt="" width={20} height={14} />
            <div className={styles.imgSmall}>
              <Image
                src={buttonImageSource}
                alt=""
                width={10}
                height={10}
                className={styles.fitmentIcon}
              />
            </div>
          </>
        )}
      </div>
      <div className={styles.descContainer}>
        <span>{buttonText}</span>
      </div>
    </>
  );

  if (vehicleFitment === 'MAY_NOT_FIT') {
    return (
      <div className={cx(styles.mayNotFitContainer, styles.vehicleFit, styles.fitmentButton)}>
        <Icon svg={Warning} color="warning" size={6} />
        <Text variant="small-body" weight="bold">
          {labels.mayNotFit}
        </Text>
      </div>
    );
  }

  return (
    <div className={cx(styles.root, customClass?.container)}>
      {isDoesNotFit ? (
        <div
          className={cx(
            azCommonStyles['az-body-2-regular'],
            styles.vehicleFit,
            styles.fitmentButton,
            isList ? styles['fitmentButton--list'] : styles['fitmentButton--grid'],
            buttonClassName,
            customClass?.button,
          )}
          data-testid={testId}
          role="group"
          aria-label={
            vehicleDisplayName
              ? `${labels.label_doesnt_fit_your} ${vehicleDisplayName}`
              : labels.label_doesnt_fit_vehicle
          }
        >
          {descChildren}
        </div>
      ) : (
        <Button
          variant="ghost"
          className={cx(
            azCommonStyles['az-body-2-regular'],
            styles.vehicleFit,
            styles.fitmentButton,
            isList ? styles['fitmentButton--list'] : styles['fitmentButton--grid'],
            buttonClassName,
            customClass?.button,
          )}
          onClick={fitmentBadgeClickCallback}
          data-testid={testId}
          aria-label={buttonText ? '' : ariaLabel ? ariaLabel : ''}
          disabled={!toggleShowYmme || isConfigurableSku}
        >
          {descChildren}
        </Button>
      )}
    </div>
  );
};

export default ACESFitmentButton;
